import { computed, readonly, ref, shallowRef, toValue } from "vue";
import { getCategory } from "@/core/api/graphql";
import { globals } from "@/core/globals";
import { getCategoryRoute, Logger, isActiveRoute } from "@/core/utilities";
import type { ExtendedQueryCategoryArgsType } from "@/core/api/graphql";
import type { Category } from "@/core/api/graphql/types";
import type { IMarkedCategory } from "@/core/types";
import type { RouteLocationNormalizedLoaded } from "vue-router";

function markActiveCategoryTree(
  category?: Category,
  currentRoute?: RouteLocationNormalizedLoaded,
): IMarkedCategory | undefined {
  if (!category || !currentRoute) {
    return;
  }

  function markRecursively(cat: Category): IMarkedCategory {
    // Innovadis custom check added for mega menu url and image for promotional stuff
    const megaMenuUrlProperty = "mega_menu_url";
    const megaMenuUrl = cat?.properties?.find((item) => item.name === megaMenuUrlProperty);

    const megaMenuImageGroup = "Mega Menu Image";
    const megaMenuImage = cat?.images?.find((item) => item.group === megaMenuImageGroup) ?? null;

    const route = getCategoryRoute(cat);
    const children = cat.childCategories?.map(markRecursively) ?? [];

    const isSelfActive = isActiveRoute(route, currentRoute as RouteLocationNormalizedLoaded);

    const isChildActive = children.some((c) => c.isActive);

    return {
      ...cat,
      childCategories: children,
      // Innovadis custom
      level: cat.level,
      megaMenuUrl: megaMenuUrl,
      megaMenuImage: megaMenuImage,

      isActive: isSelfActive || isChildActive,
    };
  }

  return markRecursively(category);
}
import type { MaybeRefOrGetter } from "vue";

export function useCategory(options: { currencyCodeOverride?: MaybeRefOrGetter<string | undefined> } = {}) {
  const loading = ref(false);
  const category = shallowRef<Category>();

  const { catalogId } = globals;

  async function fetchCategory(payload: Omit<ExtendedQueryCategoryArgsType, "storeId">) {
    loading.value = true;
    try {
      const data = await getCategory(payload, { currencyCodeOverride: toValue(options.currencyCodeOverride) });

      if (data) {
        category.value = {
          ...data.category,
          // Innovadis custom check on prio
          childCategories:
            data.childCategories.childCategories?.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)) ?? [],
          name: data.category?.name,
          id: data.category?.id || catalogId,
        };

        return data;
      } else {
        resetState();
      }
    } catch (e) {
      Logger.error(`${useCategory.name}.${fetchCategory.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function resetState() {
    loading.value = false;
    category.value = undefined;
  }

  return {
    fetchCategory,
    markActiveCategoryTree,
    loading: readonly(loading),
    category: computed(() => category.value),
  };
}
